import React from "react";
import { useParams, Link } from "react-router-dom";
import {
  Crosshair, KeyRound, CreditCard, Home as HomeIcon, Globe2,
  Download, ShieldCheck, Zap, Lock, ArrowRight, ChevronRight, Cpu,
} from "lucide-react";

/** Map of slug → tutorial content. One file, 5 routes — DRY. */
const TUTORIALS = {
  scalp: {
    icon: Crosshair,
    title: "Scalp Mode",
    tagline: "Auto-buy snipes the moment a card drops.",
    danger: true,
    intro:
      "Scalp mode runs Smart Action pipelines — trigger → filter → action — to detect restocks, price drops, and listings that match your targets, then auto-buys them from your home IP using real Playwright checkout adapters.",
    bullets: [
      "7 trigger types (restock, price-drop, scheduled, manual, task result, checkout feed, product monitor)",
      "Visual filter builder (price, retailer, deal score, region)",
      "Auto-checkout for TCGplayer, Pokemon Center, JB Hi-Fi, Target, BIG W, Kmart AU, EB Games AU",
      "1-Click Cart adds items directly to the real retailer cart",
      "Guest + Account checkout modes (use a saved store login OR check out as guest with shipping profile)",
    ],
    why:
      "Real auto-buy requires a real browser running on a residential IP — that's only possible in the desktop app. The cloud can't checkout without getting blocked by Akamai/DataDome.",
  },
  accounts: {
    icon: KeyRound,
    title: "Store Accounts",
    tagline: "Save your retailer logins, encrypted on your machine.",
    intro:
      "Store your TCGplayer / Pokemon Center / JB Hi-Fi / Target etc. account credentials inside the desktop vault so the auto-buyer can log in without you typing your password every time.",
    bullets: [
      "AES-128 (Fernet) encryption with a key only your machine knows",
      "Credentials never leave your computer — not even to our servers",
      "Test Connection button verifies the login works against the real retailer",
      "Tie each account to a Scalp profile for instant checkout",
    ],
    why:
      "We deliberately keep credentials off our cloud servers. If thecardradar.com ever gets breached, your retailer accounts are still safe.",
  },
  payments: {
    icon: CreditCard,
    title: "Payment Methods",
    tagline: "Encrypted card vault for one-click checkout.",
    intro:
      "Save your debit/credit cards locally so the auto-buyer can autofill payment details during a snipe without typing them in.",
    bullets: [
      "AES-128 (Fernet) encryption — your card numbers are never visible in plaintext",
      "Default card per profile / per retailer for quick switching",
      "Full PAN / CVV typed only into the real retailer's checkout iframe (PCI safe)",
      "Cards stored 100% locally — never POSTed to our servers",
    ],
    why:
      "Our cloud servers should never see your card. By restricting this to the desktop app, you eliminate the entire breach risk that's tanked other purchase bots.",
  },
  shipping: {
    icon: HomeIcon,
    title: "Shipping Profiles",
    tagline: "Address book for guest checkouts.",
    intro:
      "Save shipping addresses so guest-checkout auto-buys autofill the form without you typing it every time. Great for routing to mail forwarders, family, or sneaker farms.",
    bullets: [
      "Multiple profiles (home, parents', mail forwarder, freight forwarder)",
      "Per-profile default flag",
      "Encrypted at rest",
      "Wired into the Smart Action `auto_buy` step",
    ],
    why:
      "Same security model as Accounts and Payments — shipping addresses stay on your machine. Cloud has zero visibility into where your packages go.",
  },
  proxies: {
    icon: Globe2,
    title: "Proxies",
    tagline: "Route checkout traffic through residential IPs.",
    intro:
      "Configure proxy servers (residential preferred) per region so your checkout adapter requests look like normal home traffic and not flagged datacenter traffic.",
    bullets: [
      "Per-region routing (AU proxy for AU retailers, US for US retailers)",
      "HTTP, HTTPS, SOCKS5 support",
      "Auto-rotation per scrape cycle",
      "Test endpoint validates each proxy before use",
      "Failover when a proxy returns 403/blocked",
    ],
    why:
      "Even from your home IP, retailers may eventually fingerprint and block you after a few wins. A clean residential proxy pool keeps you below the radar.",
  },
};

export default function DesktopRequiredPage() {
  const params = useParams();
  // useParams returns the wildcard match; pick the first segment that matches a key
  const slug =
    Object.keys(TUTORIALS).find((k) => (params["*"] || "").startsWith(k)) ||
    Object.keys(TUTORIALS).find((k) => Object.values(params).some((v) => v && v.includes(k))) ||
    "scalp";
  const t = TUTORIALS[slug];
  const Icon = t.icon;

  return (
    <div className="px-6 py-6 max-w-5xl mx-auto" data-testid={`desktop-required-${slug}`}>
      {/* Hero */}
      <div className="mb-8">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-mono text-[#737373] mb-3">
          <Lock className="w-3 h-3" />
          Desktop only
          <ChevronRight className="w-3 h-3" />
          {t.title}
        </div>
        <div className="flex items-start gap-4">
          <div
            className={`w-14 h-14 border ${t.danger ? "border-[#EF4444]" : "border-[#EAB308]"} flex items-center justify-center ${t.danger ? "bg-[#EF4444]/10" : "bg-[#EAB308]/10"} shrink-0`}
          >
            <Icon className={`w-7 h-7 ${t.danger ? "text-[#EF4444]" : "text-[#EAB308]"}`} />
          </div>
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tighter leading-none mb-2">
              {t.title}
            </h1>
            <p className="text-[#A3A3A3] text-base leading-relaxed">{t.tagline}</p>
          </div>
        </div>
      </div>

      {/* Why-this-is-desktop-only callout */}
      <div className="bg-[#EAB308]/5 border border-[#EAB308]/30 px-4 py-3 rounded-sm mb-6 flex gap-3" data-testid="why-desktop-only">
        <ShieldCheck className="w-5 h-5 text-[#EAB308] shrink-0 mt-0.5" />
        <div className="text-[12px] leading-relaxed text-[#D4D4D4]">
          <strong className="text-white">Why isn&apos;t this in the web app?</strong> {t.why}
        </div>
      </div>

      {/* What you'll get */}
      <div className="bg-[#0d0d0d] border border-[#262626] p-5 rounded-sm mb-6">
        <h2 className="font-heading text-xs uppercase tracking-[0.2em] font-bold text-[#EAB308] mb-3">
          What you unlock on install
        </h2>
        <p className="text-sm text-[#A3A3A3] leading-relaxed mb-4">{t.intro}</p>
        <ul className="space-y-2">
          {t.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-white">
              <Zap className="w-3.5 h-3.5 text-[#EAB308] shrink-0 mt-1" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* 3-step install */}
      <div className="bg-[#0d0d0d] border border-[#262626] p-5 rounded-sm mb-6">
        <h2 className="font-heading text-xs uppercase tracking-[0.2em] font-bold text-[#EAB308] mb-4">
          Install in 3 steps
        </h2>
        <ol className="space-y-4">
          <Step n="01" title="Download the installer">
            One-click .exe builder; everything (Python runtime, Chromium, your encrypted vault) bundled
          </Step>
          <Step n="02" title="Run the installer, then launch CardRadar">
            The installer drops CardRadar into Program Files. Launching it boots a local server on your machine and opens the app at <code className="text-[#EAB308]">localhost:8001</code>. Sign in with your CardRadar email + password.
          </Step>
          <Step n="03" title="License check + unlock">
            The app verifies your subscription against <code className="text-[#EAB308]">thecardradar.com</code>, then all desktop-only features (Scalp, Accounts, Payments, Shipping, Proxies) become available.
          </Step>
        </ol>
      </div>

      {/* CTA */}
      <Link
        to="/app/desktop"
        data-testid="desktop-required-cta"
        className="flex items-center justify-center gap-2 w-full bg-[#EAB308] hover:bg-[#FBBF24] text-black px-6 py-4 rounded-sm font-heading font-black uppercase tracking-wider text-sm transition-colors"
      >
        <Download className="w-5 h-5" />
        Download CardRadar Desktop
        <ArrowRight className="w-5 h-5" />
      </Link>

      <div className="mt-6 text-center text-[11px] text-[#525252] flex items-center justify-center gap-2">
        <Cpu className="w-3 h-3" />
        Windows 10/11 · 64-bit · Free with your subscription
      </div>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <li className="flex gap-4">
      <div className="font-heading text-3xl font-black text-[#EAB308] leading-none w-12 shrink-0">{n}</div>
      <div>
        <div className="font-heading text-sm font-bold uppercase tracking-wider mb-1">{title}</div>
        <div className="text-xs text-[#A3A3A3] leading-relaxed">{children}</div>
      </div>
    </li>
  );
}
